// ────────────────────────────────────────────────────────────────────────────
// Profile missing-dex sample (2026-06-27 regression/improvement).
//
// The Profile priority-dex sprite list can toggle between CAUGHT and MISSING.
// The MISSING view shows a RANDOM subset of the species the trainer is still
// missing. That subset must be:
//   - chosen ONCE per app launch / cold start,
//   - STABLE while the user navigates away and back and while they toggle
//     caught<->missing within the same session,
//   - RESHUFFLED only on a fresh app launch (a new module evaluation).
//
// We do NOT store an explicit subset (the missing set shrinks as the trainer
// records catches). Instead we fix ONE random seed at module load and derive a
// deterministic per-id weight from it. The missing ids are ordered by that
// fixed weight and sliced — so the ordering basis never changes mid-session,
// while membership stays current. A fresh app launch re-evaluates the module
// and picks a new seed, reshuffling the sample.
// ────────────────────────────────────────────────────────────────────────────

// Seed fixed once per app launch (module evaluation). The `|| 1` guards the
// (astronomically unlikely) all-zero draw so the hash never degenerates.
let sessionSeed = (Math.floor(Math.random() * 0x7fffffff) >>> 0) || 1;

/** Test-only: pin the session seed so sampled output is deterministic. */
export function __setMissingSampleSeed(seed: number): void {
  sessionSeed = (seed >>> 0) || 1;
}

/** Deterministic xorshift hash of (seed, id) → [0, 1). */
function weight(seed: number, id: number): number {
  let x = (seed ^ Math.imul(id, 0x9e3779b1)) >>> 0;
  x ^= x << 13; x >>>= 0;
  x ^= x >>> 17;
  x ^= x << 5; x >>>= 0;
  return x / 0xffffffff;
}

/**
 * Return a session-stable random subset of `missingIds`, capped to `limit`.
 * The selection is deterministic for the lifetime of the app launch and the
 * returned ids are sorted ascending for a tidy grid.
 */
export function stableMissingSample(
  missingIds: readonly number[],
  limit: number,
): number[] {
  return [...missingIds]
    .sort((a, b) => weight(sessionSeed, a) - weight(sessionSeed, b))
    .slice(0, limit)
    .sort((a, b) => a - b);
}
